import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { api, HttpError } from "../api/rest";
import type { FollowMode } from "../api/types";
import {
  asFollowMode,
  FOLLOW_MODES,
  modeAllowed,
  modeHint,
  modeLabel,
  raisesRisk,
} from "../lib/signals";

/**
 * Переключатель режима ОДНОГО сценария.
 *
 * # Чем отличается от общего
 *
 * Ничем по правилам и всем по области. Действующий режим сценария — строгий
 * МИНИМУМ четырёх величин: потолка сервиса (`FOLLOW_MODE`), потолка сценария в
 * `FOLLOW_SCENARIOS`, общего ключа оператора и ключа этого сценария. Каждый умеет
 * только ПОНИЗИТЬ. Общий переключатель наверху остаётся и гасит все сценарии
 * разом: разбирать их по одному в момент, когда что-то пошло не так, оператор не
 * должен.
 *
 * Потолок, который здесь показан, — уже минимум `FOLLOW_MODE` и режима сценария:
 * его считает трейдер и отдаёт нотой. Считать его в браузере значило бы завести
 * второе место, где он может разойтись с боевым.
 *
 * # Асимметрия подтверждения — та же
 *
 * Понизить — один клик, поднять — два, и второй шаг сам себя отменяет. Ошибка в
 * сторону «меньше торговли» стоит пропущенных сигналов, ошибка в другую сторону
 * стоит денег.
 */
const CONFIRM_MS = 5000;

export function ScenarioModeSwitch({ id, ceiling }: { id: string; ceiling: string | undefined }) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<FollowMode | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mode = useQuery({
    queryKey: ["scenario-mode", id],
    queryFn: () => api.scenarioMode(id),
    refetchInterval: 15_000,
  });

  useEffect(() => {
    if (!pending) return;
    const timer = window.setTimeout(() => setPending(null), CONFIRM_MS);
    return () => window.clearTimeout(timer);
  }, [pending]);

  const current = mode.data?.follow_mode.mode ?? null;
  const set = mode.data?.follow_mode.set ?? false;

  async function apply(next: FollowMode) {
    if (raisesRisk(current, next) && pending !== next) {
      setPending(next);
      return;
    }
    setPending(null);
    setError(null);
    try {
      await api.setScenarioMode(id, next);
      void queryClient.invalidateQueries({ queryKey: ["scenario-mode", id] });
    } catch (err) {
      setError(err instanceof HttpError ? err.message : "не удалось записать режим");
    }
  }

  return (
    <span className="flex flex-wrap items-center gap-1">
      {FOLLOW_MODES.map((m) => {
        const allowed = modeAllowed(m, asFollowMode(ceiling));
        // «Выставлено здесь» и «действует потолок» — разные состояния, и подсветка
        // обязана их различать: иначе оператор не отличит сценарий, который он
        // сам понизил, от сценария, который никогда и не поднимался.
        const active = set && current === m;
        return (
          <button
            key={m}
            type="button"
            disabled={!allowed}
            onClick={() => void apply(m)}
            title={
              allowed
                ? `${modeHint(m)} — только для сценария ${id}`
                : ceiling
                  ? `выше потолка сценария (${ceiling})`
                  : "потолок неизвестен: трейдер не отвечает"
            }
            className={`rounded border px-1.5 py-0.5 text-[11px] disabled:opacity-40 ${
              active
                ? "border-[var(--color-accent)] text-[var(--color-accent)]"
                : "border-[var(--color-border)] text-[var(--color-muted)]"
            }`}
          >
            {pending === m ? "точно?" : modeLabel(m)}
          </button>
        );
      })}
      {error && <span className="text-[11px] text-[var(--color-sell)]">{error}</span>}
    </span>
  );
}
